import { useState, useEffect } from 'react';
import { FiChevronDown, FiChevronUp, FiCalendar, FiEye } from 'react-icons/fi';
import InvoiceModal from '../Modal/InvoiceModal';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import SearchBar from 'components/shared/SearchBar';

interface Invoice {
  id: string;
  invoiceNumber: string;
  customerName: string;
  subscriptionType: string;
  inoviceDate: string;
  amount: string;
  dueDate: string;
  paymentStatus: string;
  paymentMethod: string;
}

interface InvoicesFiltersProps {
  setRefreshMode: (value: string) => void;
  setFilters: (filters: any) => void;
  setViewInvoice: (invoice: Invoice | null) => void;
  filters: {
    startDate: Date | null;
    endDate: Date | null;
    customerName: string;
    subscriptionType: string;
    paymentStatus: string;
    paymentMethod: string,
  };
  invoices: Invoice[];
}

interface DropdownOption {
  id: string;
  label: string;
}

export default function InvoicesFilters({ 
  setRefreshMode, 
  setFilters, 
  filters,
  setViewInvoice,
  invoices 
}: InvoicesFiltersProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const customerName: DropdownOption[] = [
    { id: 'customer1', label: 'Jhon Doe' },
    { id: 'customer2', label: 'Michael Broe' },
    { id: 'customer3', label: 'Jane Smith' }  
  ];

  const subscriptionTypes: DropdownOption[] = [
    { id: 'basic', label: 'Basic' },
    { id: 'premium', label: 'Premium' },
    { id: 'enterprise', label: 'Enterprise' }
  ];

  const paymentStatuses: DropdownOption[] = [
    { id: 'paid', label: 'Paid' },
    { id: 'pending', label: 'Pending' },
    { id: 'overdue', label: 'Overdue' }
  ];

  const paymentMethods: DropdownOption[] = [
    { id: 'creditCard', label: 'Credit Card' },
    { id: 'debitCard', label: 'Debit Card' },
    { id: 'bankTransfer', label: 'Bank Transfer' }
  ];

  const handleDropdownClick = (dropdownName: string) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  const handleOptionSelect = (dropdownName: string, value: string) => {
    setFilters((prev: any) => ({ ...prev, [dropdownName]: value }));
    setOpenDropdown(null);
  };

  const clearFilters = () => {
    setFilters({
      customerName: '',
      subscriptionType: '',
      paymentStatus: '',
      paymentMethod: '',
      startDate: null,
      endDate: null,
      search: ''
    });
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setFilters((prev: any) => ({
      ...prev,
      search: value
    }));
  };

  const handleViewLatestInvoice = () => {
    if (invoices && invoices.length > 0) {
      // Find the latest invoice based on invoice date
      const latestInvoice = [...invoices].sort((a, b) => 
        new Date(b.inoviceDate).getTime() - new Date(a.inoviceDate).getTime()
      )[0];
      setViewInvoice(latestInvoice);
    }
  };

  useEffect(() => {
    setFilters((prev: any) => ({
      ...prev,
      search: searchTerm
    }));
  }, [searchTerm]);

  const Dropdown = ({ name, options, label }: { name: string; options: DropdownOption[]; label: string }) => (
    <div className="relative">
      <button
        onClick={() => handleDropdownClick(name)}
        className="flex items-center justify-between bg-[#2A2A2A] rounded-md px-3 py-2 text-gray-400 hover:bg-[#3a3a3a33]"
        style={{
          minWidth: '140px',
          maxWidth: '140px',
        }}
      >
        <span className="truncate mr-2">
          {filters[name as keyof typeof filters]?.toString() || label}
        </span>
        {openDropdown === name ? <FiChevronUp className="flex-shrink-0" /> : <FiChevronDown className="flex-shrink-0" />}
      </button>

      {openDropdown === name && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpenDropdown(null)}
          />
          <div className="absolute top-full left-0 mt-2 w-48 bg-[#2A2A2A] rounded-md shadow-lg z-20 py-1 border border-white/[0.08]">
            <button
              disabled
              className="w-full text-left px-4 py-2 text-gray-500 bg-[#2A2A2A] cursor-default truncate opacity-50"
            >
              {label}
            </button>
            
            <div className="border-b border-white/[0.08] my-1"></div>
            
            {options.map((option) => (
              <button
                key={option.id}
                onClick={() => handleOptionSelect(name, option.label)}
                className="w-full text-left px-4 py-2 text-gray-400 hover:bg-[#3a3a3a33] hover:text-white truncate"
                title={option.label}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <SearchBar
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search invoices..."
          />
          
          <div className="relative">
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="flex items-center justify-between bg-[#2A2A2A] rounded-md px-3 py-2 text-gray-400 hover:bg-[#3a3a3a33]"
              style={{
                minWidth: '140px',
                maxWidth: '200px',
              }}
            >
              <div className="flex items-center gap-2 truncate">
                <FiCalendar className="flex-shrink-0" />
                <span className="truncate">
                  {(filters.startDate && filters.endDate)
                    ? `${new Date(filters.startDate).toLocaleDateString()} - ${new Date(filters.endDate).toLocaleDateString()}`
                    : 'Select Duration'
                  }
                </span>
              </div>
            </button>
            
            {showDatePicker && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowDatePicker(false)}
                />
                <div className="absolute top-full left-0 mt-2 z-20 bg-[#2A2A2A] rounded-lg p-4 border border-white/[0.08]">
                  <DatePicker
                    selectsRange={true}
                    startDate={filters.startDate}
                    endDate={filters.endDate}
                    onChange={(update: [Date | null, Date | null]) => {
                      setFilters((prev: any) => ({
                        ...prev,
                        startDate: update[0],
                        endDate: update[1]
                      }));
                      if (update[0] && update[1]) {
                        setShowDatePicker(false);
                      }
                    }}
                    inline
                  />
                </div>
              </>
            )}
          </div>
          
          <Dropdown
            name="customerName"
            options={customerName}
            label="Customer Name"
          />

          <Dropdown
            name="subscriptionType"
            options={subscriptionTypes}
            label="Subscription type"
          />
          
          <Dropdown
            name="paymentStatus"
            options={paymentStatuses}
            label="Payment Status"
          />

          <Dropdown
            name="paymentMethod"
            options={paymentMethods}
            label="Payment Method"
          />

          {Object.values(filters).some(Boolean) && (
            <button 
              onClick={clearFilters}
              className="text-red-400 hover:text-red-300"
            >
              Clear all
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* <button
            onClick={handleViewLatestInvoice}
            className="flex items-center gap-2 bg-[#2A2A2A] hover:bg-[#3a3a3a33] rounded-md px-4 py-2 text-gray-400 hover:text-white"
          >
            <FiEye className="w-4 h-4" />
             Invoice Detail
          </button> */}
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 bg-[#2A2A2A] hover:bg-[#3a3a3a33] rounded-md px-4 py-2 text-gray-400 hover:text-white"
          >
            + Add Invoice
          </button>
        </div>
      </div>

      <InvoiceModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        mode="add"
        setRefreshMode={setRefreshMode}
      />
    </>
  );
} 